/**
 * หน้าจอ Product Master — list / รายละเอียด / สร้างใหม่ / คำสั่งอนุมัติ
 *
 * คำสั่งส่งอนุมัติ อนุมัติ และไม่อนุมัติ อยู่บนหน้ารายละเอียด
 * (`ProductTransitionPanel`) ส่วนการแก้ไข (`PATCH`) เป็นหน้าจอแยกของมันเอง
 * (`ProductEditView`) และคำขอเปลี่ยนแปลง — ซึ่งรวมการปิดใช้งานตาม D-042 —
 * เป็นหน้าจอแยกอีกตัว (`ProductChangeRequestView`) เพราะมันเป็นคนละ resource
 * ไม่ใช่ transition ตัวที่สี่ของ product
 *
 * การ gate สิทธิ์เป็นแบบเดียวกับที่ `WO-P1-M10-001` วางไว้: อ่าน flag จาก
 * `modules` ของ permission set ที่เซิร์ฟเวอร์ส่งมาเท่านั้น ผ่าน `can()`
 * **ห้าม derive กติกา ADMIN/AUDITOR ซ้ำจาก `system_roles`** และทุกสถานะที่ไม่ใช่
 * `ready` แปลว่าไม่มีสิทธิ์
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย การซ่อนปุ่ม "สร้าง" ไม่ได้
 * ป้องกันอะไรเลย — เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ ที่ซ่อนเพราะไม่อยาก
 * ให้ผู้ใช้กรอกฟอร์มยาว ๆ แล้วโดนปฏิเสธตอนกดบันทึก
 */

import { useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { ProductChangeRequestView } from './ProductChangeRequestView';
import { ProductCreateView } from './ProductCreateView';
import { ProductDetailView } from './ProductDetailView';
import { ProductEditView } from './ProductEditView';
import { ProductListView } from './ProductListView';
import { createSearch, detailSearch, resolveProductView, type ProductView } from './productView';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import type { Breadcrumb } from '../HeaderBar';
import { useTrailingCrumbs } from '../desktopContext';
import { PRODUCT_MASTER_MODULE } from '../../lib/products/types';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';

export interface ProductMasterScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

/**
 * crumb ท้ายสุดที่หน้าจอนี้ contribute เอง — `ProductMasterScreen` คือ reference
 * implementation เดียวของกลไกนี้ (`WO-P2-M11-004`, D-107 ข้อ 6)
 *
 * label เป็นค่าคงที่ ไม่พึ่งข้อมูลที่โหลดมาจากเซิร์ฟเวอร์ (เช่นรหัสสินค้าในหน้า
 * รายละเอียด) โดยตั้งใจ: `view` มาจาก search param ล้วน ๆ รู้ได้ตั้งแต่ render แรก
 * ไม่ต้องรอ fetch เสร็จ ส่วนหน้า list ไม่มี trailing crumb เพราะ crumb ของตัวหน้าจอ
 * เอง ("ข้อมูลสินค้า (Product Master)") ที่ `DesktopShell` สร้างจาก route ก็ตรงอยู่แล้ว
 */
function trailingCrumbFor(viewKind: ProductView['kind']): readonly Breadcrumb[] {
  switch (viewKind) {
    case 'create':
      return [{ label: 'สร้างข้อมูลสินค้าใหม่' }];
    case 'detail':
      return [{ label: 'รายละเอียดสินค้า' }];
    case 'edit':
      return [{ label: 'แก้ไขข้อมูลสินค้า' }];
    case 'change-requests':
      return [{ label: 'คำขอเปลี่ยนแปลงข้อมูลสินค้า' }];
    case 'list':
      return [];
  }
}

function CreateNotOfferedNotice() {
  return (
    <div className="cap-page">
      <div className="cap-notice" data-testid="product-create-unavailable" role="status">
        <StatusBadge tone="warning">ไม่ได้เปิดฟอร์มให้</StatusBadge>
        <h1 className="cap-notice__title">
          สิทธิ์ชุดนี้ไม่มีการสร้างข้อมูลสินค้า
          <InfoHint
            label="เกี่ยวกับการไม่เปิดฟอร์มที่นี่"
            text="การไม่เปิดฟอร์มเป็นเพียง UX เพื่อไม่ให้กรอกงานที่จะถูกปฏิเสธตอนบันทึก — ผู้ตัดสินจริงคือ backend ทุกคำขอเสมอ"
          />
        </h1>
        <p className="cap-notice__text">
          สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag <code className="cap-table__code">can_create</code>{' '}
          ของโมดูล <code className="cap-table__code">PRODUCT_MASTER</code> หน้าจอนี้จึงไม่เปิดฟอร์มให้กรอก
        </p>
      </div>
    </div>
  );
}

function EditNotOfferedNotice() {
  return (
    <div className="cap-page">
      <div className="cap-notice" data-testid="product-edit-unavailable" role="status">
        <StatusBadge tone="warning">ไม่ได้เปิดฟอร์มให้</StatusBadge>
        <h1 className="cap-notice__title">
          สิทธิ์ชุดนี้ไม่มีการแก้ไขข้อมูลสินค้า
          <InfoHint
            label="เกี่ยวกับการไม่เปิดฟอร์มที่นี่"
            text="การไม่เปิดฟอร์มเป็นเพียง UX เพื่อไม่ให้กรอกงานที่จะถูกปฏิเสธตอนบันทึก — ผู้ตัดสินจริงคือ backend ทุกคำขอเสมอ"
          />
        </h1>
        <p className="cap-notice__text">
          สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag <code className="cap-table__code">can_edit</code>{' '}
          ของโมดูล <code className="cap-table__code">PRODUCT_MASTER</code> หน้าจอนี้จึงไม่เปิดฟอร์มให้แก้
        </p>
      </div>
    </div>
  );
}

export function ProductMasterScreen({
  permissions,
  apiBaseUrl,
  fetchImpl,
}: ProductMasterScreenProps) {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const setTrailingCrumbs = useTrailingCrumbs();

  const view = resolveProductView(searchParams);
  const canCreate = can(permissions, PRODUCT_MASTER_MODULE, 'CREATE');

  // sub-page ของหน้าจอนี้เป็น search param ไม่ใช่ nested route (ดูหมายเหตุของ
  // `trailingCrumbFor` ด้านบน) จึงต้องบอก `DesktopShell` เองว่าตอนนี้อยู่ลึกแค่ไหน
  useEffect(() => {
    setTrailingCrumbs(trailingCrumbFor(view.kind));
  }, [view.kind, setTrailingCrumbs]);

  if (view.kind === 'edit') {
    // สถานะของ record ตัดสินได้หลังโหลดเท่านั้น จึงเป็นหน้าที่ของ `ProductEditView`
    // ส่วนที่ตัดสินได้ตั้งแต่ตรงนี้คือ flag ที่เซิร์ฟเวอร์ส่งมาแล้ว
    if (!can(permissions, PRODUCT_MASTER_MODULE, 'EDIT')) {
      return <EditNotOfferedNotice />;
    }
    return (
      <ProductEditView
        productId={view.productId}
        apiBaseUrl={apiBaseUrl}
        fetchImpl={fetchImpl}
      />
    );
  }

  if (view.kind === 'change-requests') {
    // ไม่มี gate เพิ่มตรงนี้โดยตั้งใจ: `GET .../change-requests` ต้องการแค่สิทธิ์ VIEW
    // ซึ่งเป็นสิทธิ์เดียวกับที่พาผู้ใช้มาถึงหน้าจอกลุ่มนี้ได้ตั้งแต่แรก ส่วนการเสนอ
    // คำขอ (EDIT) และการอนุมัติ (APPROVE) ถูก gate ในหน้าจอนั้นทีละส่วน เพราะคนที่
    // ดูได้อย่างเดียวก็ยังต้อง "เห็นว่ามีคำขอค้างอยู่" ได้
    return (
      <ProductChangeRequestView
        productId={view.productId}
        permissions={permissions}
        apiBaseUrl={apiBaseUrl}
        fetchImpl={fetchImpl}
      />
    );
  }

  if (view.kind === 'detail') {
    return (
      <ProductDetailView
        productId={view.productId}
        permissions={permissions}
        apiBaseUrl={apiBaseUrl}
        fetchImpl={fetchImpl}
      />
    );
  }

  if (view.kind === 'create') {
    if (!canCreate) {
      return <CreateNotOfferedNotice />;
    }
    return (
      <ProductCreateView
        permissions={permissions}
        apiBaseUrl={apiBaseUrl}
        fetchImpl={fetchImpl}
        onCreated={(product) => {
          // ไปที่รายการที่เพิ่งสร้าง แบบ replace เพื่อให้ปุ่ม back ไม่ย้อนกลับไป
          // ที่ฟอร์มซึ่งถูกบันทึกไปแล้ว (แล้วชวนให้กดบันทึกซ้ำ)
          navigate({ pathname, search: detailSearch(product.product_id) }, { replace: true });
        }}
      />
    );
  }

  return (
    <ProductListView
      apiBaseUrl={apiBaseUrl}
      fetchImpl={fetchImpl}
      canCreate={canCreate}
      createHref={createSearch()}
    />
  );
}
